<template>
  <content-container :table-row-count="tableData.length">
    <!-- 搜索条件区域 -->
    <search-container>
      <search-col>
        <el-input v-model="params.name" placeholder="报名人员" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-input v-model="params.idNo" placeholder="身份证号" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-input v-model="params.cardNo" placeholder="银行卡号" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-input v-model="params.phone" placeholder="手机号" clearable @keydown="onEnter" />
      </search-col>

      <template #buttons>
        <el-button @click="resetForm">
          <svg-icon icon="tabler:refresh" />重置
        </el-button>
        <el-button type="primary" @click="doSearch">
          <svg-icon icon="lucide:search" />查询
        </el-button>
      </template>
    </search-container>
    
    <!-- 表格数据 -->
    <el-table v-loading="loading" class="normal-table" :border="true" :scrollbar-always-on="true" :data="tableData">
      <el-table-column align="left" prop="taskNo" label="审批单编号" :min-width="120" show-overflow-tooltip />
      <el-table-column align="left" prop="name" label="报名人员" :min-width="120" show-overflow-tooltip />
      <el-table-column align="left" prop="idNo" label="身份证号" :min-width="120" show-overflow-tooltip />
      <el-table-column align="left" prop="cardNo" label="银行卡号" :min-width="120" show-overflow-tooltip />
      <el-table-column align="left" prop="phone" label="手机号" :min-width="120" />

      <el-table-column
        v-if="isShowAction&&buttons.length > 0"
        align="center"
        prop="actions"
        fixed="right"
        label="操作"
      >
        <template #default="scope">
          <table-row-buttons :buttons="buttons" :row="scope.row" />
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 底部分页 -->
    <template #footer>
      <el-pagination
        v-model:current-page="params.page"
        v-model:page-size="params.size"
        :page-sizes="[defaultPageSize, 25, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        background
        @size-change="onSizeChange"
        @current-change="onCurrentPageChange"
      />
    </template>

    <RegistrationConfirm :visible="showRegistrationConfirm" :status="currentStatus" @close="showRegistrationConfirm=false" @confim="handleConfim" />
  </content-container>
</template>

<script setup lang="ts">
import RegistrationConfirm from './dialog/RegistrationConfirm.vue';
import { PropType, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { SvgIcon, TableRowButtons } from '@vue3/components';
import {
  ContentContainer,
  SearchContainer,
  SearchCol,
  useTblPermissionButtons,
  updateApproveTask,
  queryFreelanceSignUpNonAuditPage,
  queryApprovePermission,
} from '@core/main';
import { approveStatusLabel, EApproveStatus, EApproveStep, EApproveType, IDutySignUpUserNonAudit } from '@core/main/types';
import { useRoute } from 'vue-router';

defineOptions({ name: 'BrokerLaborApplicantsAwaitingApproval' });

const emit = defineEmits(['fetchData']);

defineProps({
  from: {
    type: String as PropType<'saas' | 'company' | 'agent' | 'server'>,
    default: 'saas',
  },
});
const route = useRoute();
const loading = ref(false);
const defaultPageSize = 12;
const params = reactive<any>({
  page: 1,
  size: defaultPageSize,
});
const total = ref(0);
const tableData = ref<IDutySignUpUserNonAudit[]>([]);
const showRegistrationConfirm = ref(false);
const currentStatus = ref(EApproveStatus.SUCCESS);
const currentRow = ref({} as any);
const isShowAction = ref(false);

const { buttons } = useTblPermissionButtons([
  {
    name: '通过',
    code: '',
    action: handlePass,
  },
  {
    name: '驳回',
    code: '',
    action: handleReject,
  },
]);

function resetForm() {
  for (const key in params) {
    if (!['page', 'size'].includes(key)) {
      params[key] = undefined;
    }
  }
}

function onSizeChange(size: number) {
  params.size = size;
  params.page = 1;
  fetchData();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchData();
}

function doSearch() {
  params.page = 1;
  fetchData();
}

function onEnter(e) {
  if (e.keyCode === 13) doSearch();
}

function handlePass(row) {
  currentRow.value = row;
  currentStatus.value = EApproveStatus.SUCCESS;
  showRegistrationConfirm.value = true;
}

function handleReject(row) {
  currentRow.value = row;
  currentStatus.value = EApproveStatus.REJECT;
  showRegistrationConfirm.value = true;
}

async function handleConfim(remark: string) {
  showRegistrationConfirm.value = false;
  try {
    const params = {
      taskNo: String(currentRow.value?.taskNo),
      remark,
      status: currentStatus.value,
      extraInfo: undefined,
    };

    await updateApproveTask(params);
    ElMessage.success(`${approveStatusLabel[currentStatus.value]}成功`);
    fetchData();
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function fetchData() {
  loading.value = true;

  try {
    const { data, total: all } = await queryFreelanceSignUpNonAuditPage({
      ...params,
      id: Number(route.query.taskId),
    });

    tableData.value = data;
    total.value = all;
    emit('fetchData');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  loading.value = false;
}

async function fetchApprovePermission() {

  try {
    const res = await queryApprovePermission(EApproveType.MATCH_REGISTRATION, EApproveStep.FINAL);

    isShowAction.value = res;

  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

}

async function init() {
  await fetchData();
  await fetchApprovePermission();
}
init();
</script>
